import { ComponentType, FC, MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useObservable, useObservableState } from 'observable-hooks';
import { map } from 'rxjs';

import {
	ILatLng,
	LegsPlaceView,
	PassengerLegStatusEnum,
	TripTypeEnum,
} from '@mopla/data-models';
import {
	checkIsLBODTFlexLeg,
	checkIsPRODTLeg,
	getCountdownTimer$,
	getItineraryTimingV2,
	openMapsApp,
	prepareLegAddress,
	useI18nLanguage,
	wrapDisplayName,
} from '@mopla/utils';

import { ILegDetailsContext, LegDetailsContextProvider } from './context';
import { ILegDetailsProps } from './types';

export const withLegDerivedData = (
	WrappedComponent: ComponentType<ILegDetailsProps>
) => {
	const Wrapper: FC<ILegDetailsProps> = (props) => {
		const { leg, isBookedItinerary, disabled } = props;
		const { t } = useTranslation('searchResults');
		const language = useI18nLanguage();

		const legTimings = useMemo(() => getItineraryTimingV2(leg), [leg]);

		const effectiveStartTime = legTimings.isStartTimeChanged
			? legTimings.effectiveStartTime
			: null;

		const effectiveEndTime = legTimings.isEndTimeChanged
			? legTimings.effectiveEndTime
			: null;

		/** Countdown until -15minutes from the startDateTime */
		const minutesBeforeStart$ = useObservable(() =>
			getCountdownTimer$(legTimings.effectiveStartDateObj).pipe(
				map((duration) => duration.asMinutes())
			)
		);

		const minutesBeforeStart = useObservableState(minutesBeforeStart$, null);

		const getLocationLabelClickHandler = useMemo(
			() => (location: LegsPlaceView) => (e: MouseEvent) => {
				e.preventDefault();

				openMapsApp({ lat: Number(location.lat), lng: Number(location.lng) });
			},
			[]
		);

		const ctx = useMemo<ILegDetailsContext>(() => {
			const isStartEndDateFix = Boolean(leg.timeInfo?.startAndEndTimeFixed);

			const isLBODTFlexLeg = checkIsLBODTFlexLeg(leg);
			const isPRODTLeg = checkIsPRODTLeg(leg);
			const distanceKm = (Math.round((leg.distance || 0) / 100) / 10)
				.toFixed(1)
				.toString()
				.replace('.', ',');

			let mapWaypoints: ILatLng[] | null = null;
			let showLiveMap = false;
			let walkLegText = null;

			const isThereMapWaypoints =
				(isPRODTLeg || isLBODTFlexLeg) && !isBookedItinerary && !disabled;

			if (leg.mode === TripTypeEnum.WALK) {
				walkLegText = t('text.walkTime', {
					minutes: Math.round((leg.duration || 0) / 60),
					kilometers: distanceKm,
				});
			}

			if (leg.mode === TripTypeEnum.TRANSFER_WALK) {
				walkLegText = t('text.transfer', {
					kilometers: distanceKm,
				});
			}

			if (isThereMapWaypoints) {
				mapWaypoints = [
					{ lat: +leg.from.lat, lng: +leg.from.lng },
					{ lat: +leg.to.lat, lng: +leg.to.lng },
				];
			}

			if (
				[
					TripTypeEnum.PRODT,
					TripTypeEnum.LBODTFLEX,
					TripTypeEnum.LBODT,
				].includes(leg.mode)
			) {
				/**
				 * Show the livemap when it is less than 20 minutes before the leg start
				 * In case the leg start time is passed (minutesBeforeStart===null),
				 * the BE decides whether to show the livemap or not (422 error).
				 * The BE should restrict access to the livemap since the passenger has checked in
				 * */
				showLiveMap = Boolean(
					isBookedItinerary &&
						leg.legStatus &&
						![
							PassengerLegStatusEnum.CANCELLED,
							PassengerLegStatusEnum.COMPLETED,
						].includes(leg.legStatus) &&
						minutesBeforeStart != null &&
						minutesBeforeStart <= 20
				);
			}

			const isTimeNarrow = language !== 'en';

			return {
				mode: leg.mode,
				vehicleType: leg.vehicleType,
				startPointLabel: prepareLegAddress(leg.from),
				startPointAddress: leg.from.address,
				startPointUrl: leg.from.stopLink,
				startTime: legTimings.startTime,
				endTime: legTimings.endTime,
				effectiveStartTime,
				effectiveEndTime,
				endPointLabel: prepareLegAddress(leg.to),
				endPointAddress: leg.to.address,
				endPointUrl: leg.to.stopLink,
				isStartEndDateFix,
				walkLegText,
				isTimeNarrow,
				mapWaypoints,
				disabled,
				showLiveMap,
				onStartPointClick: getLocationLabelClickHandler(leg.from),
				onEndPointClick: getLocationLabelClickHandler(leg.to),
			};
		}, [
			leg,
			isBookedItinerary,
			disabled,
			language,
			legTimings,
			effectiveStartTime,
			effectiveEndTime,
			getLocationLabelClickHandler,
			t,
			minutesBeforeStart,
		]);

		return (
			<LegDetailsContextProvider value={ctx}>
				<WrappedComponent {...props} />
			</LegDetailsContextProvider>
		);
	};

	Wrapper.displayName = wrapDisplayName(WrappedComponent, 'withLegDerivedData');

	return Wrapper;
};
